import { TRIAL_INTERVAL, TRIAL_TIMEOUT } from '@/modules/trials/constants'
import { GetStoreApiResponse, Store } from '@/modules/trials/interfaces'
import { StoresService } from '@/modules/trials/services/stores-service/service'

import { httpGet } from '@/utils/trials/http'
import { waitFor } from '@/utils/waitFor'

export class PollingService {
	private storesService: StoresService | undefined

	private interval: number
	private timeout: number

	constructor(interval = TRIAL_INTERVAL, timeout = TRIAL_TIMEOUT) {
		this.interval = interval > 0 ? interval : TRIAL_INTERVAL
		this.timeout = timeout > 0 ? timeout : TRIAL_TIMEOUT
	}

	// We can't initialize StoresService in the constructor because this service is used for both the frontend and backend
	setStoresService(storesService: StoresService) {
		this.storesService = storesService
	}

	async pollStore(storeId: string): Promise<Store> {
		const res = await Promise.race([this.continuallyGetStore(storeId), waitFor(this.timeout)])

		if (!res) {
			throw new Error('Store polling timed out')
		}

		return res
	}

	async continuallyGetStore(storeId: string): Promise<Store> {
		if (!this.storesService) {
			throw new Error('StoresService is not set')
		}

		let store: Store | undefined

		do {
			if (store) {
				await waitFor(this.interval)
			}

			store = await this.storesService.getStore(storeId)
		} while (!store || store.status === 'provisioning')

		if (store.status !== 'active') {
			throw new Error('Store could not be provisioned')
		}

		return store
	}

	async pollStoreApi(): Promise<GetStoreApiResponse> {
		const res = await Promise.race([this.continuallyGetStoreApi(), waitFor(this.timeout)])

		if (!res) {
			throw new Error('Store polling timed out')
		}

		return res
	}

	async continuallyGetStoreApi(): Promise<GetStoreApiResponse> {
		let store: GetStoreApiResponse | undefined

		do {
			if (store) {
				await waitFor(this.interval)
			}

			store = await httpGet('/api/trials/get-store/', {
				headers: {
					'Content-Type': 'application/json'
				}
			})
		} while (!store || store.storeStatus === 'provisioning')

		if (store.storeStatus !== 'active') {
			throw new Error('Store could not be provisioned')
		}

		return store
	}
}
